import React, { useState } from 'react'
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap'

import ProviderService from '../../../services/ProviderService'

export default function DuplicateClusterCard({ cluster, onResolvedChange }) {
  const [selectedCanonicalId, setSelectedCanonicalId] = useState(
    cluster.suggestedCanonicalId || cluster.providers?.[0]?.id,
  )
  const [isApplying, setIsApplying] = useState(false)
  const [isUndoing, setIsUndoing] = useState(false)
  const [error, setError] = useState(null)
  const [resolvedState, setResolvedState] = useState(null) // { actionType, undoPayloadJson, message }

  const handleMerge = async () => {
    setIsApplying(true)
    setError(null)
    try {
      const duplicateIds = (cluster.providers || []).map(p => p.id).filter(id => id !== selectedCanonicalId)

      const response = await ProviderService.mergeProviders({
        canonicalId: selectedCanonicalId,
        duplicateIds,
      })

      if (response && response.success) {
        const state = {
          actionType: 'merge',
          undoActionId: response.undoActionId,
          undoPayloadJson: response.undoPayloadJson || response.undoActionId,
          message: response.message || 'Records successfully merged.',
        }
        setResolvedState(state)
        if (onResolvedChange) onResolvedChange(cluster.clusterId, true)
      } else {
        setError(response?.message || 'Failed to merge providers.')
      }
    } catch (err) {
      setError(err?.message || 'Error occurred while merging.')
    } finally {
      setIsApplying(false)
    }
  }

  const handleUndo = async () => {
    if (!resolvedState?.undoPayloadJson && !resolvedState?.undoActionId) return
    setIsUndoing(true)
    setError(null)
    try {
      await ProviderService.undoLibraryAction({
        actionType: resolvedState.actionType,
        undoActionId: resolvedState.undoActionId,
        previousStateJson: resolvedState.undoPayloadJson,
      })
      setResolvedState(null)
      if (onResolvedChange) onResolvedChange(cluster.clusterId, false)
    } catch (err) {
      setError(err?.message || 'Failed to undo merge.')
    } finally {
      setIsUndoing(false)
    }
  }

  const handleDismiss = () => {
    setResolvedState({
      actionType: 'dismiss',
      undoPayloadJson: null,
      message: 'Marked as kept separate.',
    })
    if (onResolvedChange) onResolvedChange(cluster.clusterId, true)
  }

  const canonicalProvider = cluster.providers?.find(p => p.id === selectedCanonicalId)

  return (
    <Card className={`mb-3 border-${resolvedState ? 'success' : 'light'} shadow-sm`}>
      <Card.Header className="d-flex justify-content-between align-items-center bg-white py-2">
        <div className="d-flex align-items-center">
          <span className="mr-2" role="img" aria-label="duplicate">
            👥
          </span>
          <strong className="text-dark">Duplicate Provider Cluster</strong>
          <Badge variant="warning" className="ml-2">
            {Math.round((cluster.confidence || 0.9) * 100)}% Match
          </Badge>
        </div>
        {resolvedState && (
          <Badge variant="success" className="p-1 px-2">
            ✓ Resolved
          </Badge>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        {error && (
          <Alert variant="danger" className="py-1 small mb-2">
            {error}
          </Alert>
        )}

        {resolvedState ? (
          <div className="d-flex justify-content-between align-items-center py-2">
            <span className="text-success font-weight-bold">✓ {resolvedState.message}</span>
            {resolvedState.undoPayloadJson && (
              <Button variant="outline-secondary" size="sm" onClick={handleUndo} disabled={isUndoing}>
                {isUndoing ? 'Undoing...' : 'Undo'}
              </Button>
            )}
          </div>
        ) : (
          <>
            <p className="text-muted small mb-2">{cluster.reason}</p>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-2">
                <thead className="thead-light small">
                  <tr>
                    <th style={{ width: '10%' }}>Keep as Primary</th>
                    <th style={{ width: '35%' }}>Provider Name / Class</th>
                    <th style={{ width: '20%' }}>NPI</th>
                    <th style={{ width: '35%' }}>Address & Contact</th>
                  </tr>
                </thead>
                <tbody className="small">
                  {(cluster.providers || []).map(p => {
                    const isSelected = p.id === selectedCanonicalId
                    const addr = (p.addresses || [])[0]
                    const addrText = addr
                      ? [addr.line1, addr.city, addr.state, addr.zip].filter(Boolean).join(', ')
                      : '—'
                    return (
                      <tr key={p.id} className={isSelected ? 'table-primary font-weight-bold' : ''}>
                        <td className="text-center align-middle">
                          <Form.Check
                            type="radio"
                            name={`canonical-${cluster.clusterId}`}
                            checked={isSelected}
                            onChange={() => setSelectedCanonicalId(p.id)}
                            id={`radio-${cluster.clusterId}-${p.id}`}
                          />
                        </td>
                        <td className="align-middle">
                          <div>{p.name}</div>
                          <Badge
                            variant={p.classification === 'Individual' ? 'info' : 'secondary'}
                            className="font-weight-normal"
                          >
                            {p.classification || 'Organization'}
                          </Badge>
                        </td>
                        <td className="align-middle">{p.npi || '—'}</td>
                        <td className="align-middle text-muted">
                          <div>{addrText}</div>
                          {p.phone && <div>📞 {p.phone}</div>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-2">
              <span className="small text-muted">
                Merging will combine contacts/addresses into <strong>{canonicalProvider?.name}</strong> and archive
                duplicate rows.
              </span>
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mr-2"
                  onClick={handleDismiss}
                  disabled={isApplying}
                >
                  Keep Separate
                </Button>
                <Button variant="primary" size="sm" onClick={handleMerge} disabled={isApplying}>
                  {isApplying ? 'Merging...' : `Merge into Selected`}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}
